import React from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
} from "@chakra-ui/react";

const styles = {
    container: {
        fontFamily: "'Arial', sans-serif",
        lineHeight: "1.6",
        color: "#333",
    },
    title: {
        textAlign: "center",
        marginBottom: "20px",
        fontSize: "1.8rem",
        fontWeight: "bold",
        color: "#1a202c",
    },
    section: {
        marginBottom: "20px",
    },
    sectionTitle: {
        fontSize: "1.5rem",
        marginBottom: "10px",
        fontWeight: "bold",
        color: "#2d3748",
    },
    paragraph: {
        marginBottom: "10px",
    },
    list: {
        paddingLeft: "20px",
        marginBottom: "10px",
    },
    listItem: {
        marginBottom: "5px",
    },
};

const SubscriptionPolicyModal = ({ isOpen, onClose }) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>BeatStake Subscription Agreement</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <div style={styles.container}>
                        <h1 style={styles.title}>BeatStake Subscription Agreement</h1>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>Introduction</h2>
                            <p style={styles.paragraph}>
                                Welcome to BeatStake! This Subscription Agreement ("Agreement") outlines your access
                                to and use of the subscription services provided by BeatStake for artists and third-party
                                accounts (collectively referred to as "Subscribers"). By subscribing to any of our tiers, you
                                agree to the terms in this Agreement, as well as the BeatStake Terms of Service and Privacy Policy.
                            </p>
                            <p style={styles.paragraph}>
                                This Agreement explains the rights, responsibilities, and expectations of your subscription,
                                including payment terms, benefits, and usage limitations. It applies exclusively to subscription
                                services and does not alter the general terms for all users of the BeatStake platform.
                            </p>
                            <p style={styles.paragraph}>
                                For questions, please contact us. By subscribing, you confirm that you have read, understood,
                                and agree to this Agreement. If you do not agree, please do not subscribe.
                            </p>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>Table of Contents</h2>
                            <ol style={styles.list}>
                                <li style={styles.listItem}>Subscription Tiers and Fees</li>
                                <li style={styles.listItem}>Payment Terms</li>
                                <li style={styles.listItem}>Cancellation and Termination</li>
                                <li style={styles.listItem}>User Responsibilities</li>
                                <li style={styles.listItem}>Subscription Benefits and Limitations</li>
                                <li style={styles.listItem}>Dispute Resolution</li>
                                <li style={styles.listItem}>Modifications to Subscription Terms</li>
                                <li style={styles.listItem}>Governing Law</li>
                            </ol>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>1. Subscription Tiers and Fees</h2>
                            <p style={styles.paragraph}>
                                BeatStake offers three subscription tiers tailored to the needs of artists and third-party users:
                            </p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>
                                    <strong>Musician:</strong> $29.99/month – Access to essential features, including listing up to 2 tracks.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Musician Plus:</strong> $79.99/month – Includes Musician Tier benefits plus tools for analytics and up to 10 tracks.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Ultimate:</strong> $199.99/month – Access to all features, priority support, and 99 track listings.
                                </li>
                            </ul>
                            <p style={styles.paragraph}>
                                Subscribers can upgrade or downgrade at any time, with changes reflected in the next billing cycle.
                            </p>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>2. Payment Terms</h2>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>
                                    <strong>Automatic Renewal:</strong> Subscriptions renew automatically unless canceled in account settings.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Payment Methods:</strong> Payments are processed through Stripe. Subscribers must maintain valid payment information.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Failed Payments:</strong> Subscriptions will be suspended for failed payments until resolved.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Refund Policy:</strong> Refunds are not issued for unused portions of subscriptions or downgrades.
                                </li>
                            </ul>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>3. Cancellation and Termination</h2>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>
                                    <strong>Cancellation by User:</strong> Subscriptions can be canceled anytime through account settings.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Termination by Platform:</strong> BeatStake reserves the right to terminate subscriptions for violations of this Agreement.
                                </li>
                            </ul>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>4. User Responsibilities</h2>
                            <p style={styles.paragraph}>Subscribers must:</p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>Provide accurate and updated billing information.</li>
                                <li style={styles.listItem}>Comply with BeatStake’s platform policies, including verifying ownership of digital assets.</li>
                                <li style={styles.listItem}>Avoid sharing subscription access with unauthorized users.</li>
                                <li style={styles.listItem}>Avoid using BeatStake’s service in any manner inconsistent with the laws of the United States.</li>
                            </ul>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>5. Subscription Benefits and Limitations</h2>
                            <p style={styles.paragraph}>
                                Subscribers receive benefits based on their tier, including access to listing tools and analytics. Subscriptions are non-transferable.
                            </p>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>6. Dispute Resolution</h2>
                            <p style={styles.paragraph}>
                                For disputes, contact BeatStake support. If unresolved, disputes will proceed to mediation or arbitration.
                            </p>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>7. Modifications to Subscription Terms</h2>
                            <p style={styles.paragraph}>
                                BeatStake may update subscription tiers, fees, or benefits with 30 days' notice. Changes apply to future billing cycles.
                            </p>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>8. Governing Law</h2>
                            <p style={styles.paragraph}>
                                This Agreement is governed by Maryland law. Disputes are subject to the exclusive jurisdiction of Maryland courts.
                            </p>
                        </section>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default SubscriptionPolicyModal;